import { z } from 'zod'

/**
 * Схемы поведений бота — источник истины для `bot.behaviours` (Json в БД),
 * для API-DTO и для рантайма воркера. Перенесено из docs/05 §4.
 *
 * Единственная правка против документа: `ChunkAnchor.point` получил
 * `.default(null)`. В документе он `.nullable()` без дефолта, из-за чего
 * `ChunkAnchor.default({})` (и, следовательно, `BehaviourConfig.parse({})`)
 * не проходил бы ни по типам, ни в рантайме. Смысл сохранён: якоря нет — null.
 */

export const AntiAfk = z.object({
  enabled: z.boolean().default(true),
  intervalSec: z.tuple([z.number().int().min(5), z.number().int().max(600)]).default([25, 55]),
  actions: z
    .array(z.enum(['jump', 'lookAround', 'swingArm', 'sneakToggle', 'stepAside']))
    .default(['lookAround', 'jump', 'swingArm']),
  jitterPct: z.number().min(0).max(100).default(35) // против детекта паттерна
})
export type AntiAfk = z.infer<typeof AntiAfk>

/** Точка привязки бота в мире (координаты + измерение). */
export const AnchorPoint = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
  dimension: z.string()
})
export type AnchorPoint = z.infer<typeof AnchorPoint>

export const ChunkAnchor = z.object({
  enabled: z.boolean().default(false),
  point: AnchorPoint.nullable().default(null),
  maxDriftBlocks: z.number().default(8),
  returnWithPathfinder: z.boolean().default(true),
  onDeathReturn: z.boolean().default(true)
})
export type ChunkAnchor = z.infer<typeof ChunkAnchor>

/** Откуда берётся текст для сопоставления хука. */
export const HookSource = z.enum(['chat', 'kick', 'title', 'actionBar'])
export type HookSource = z.infer<typeof HookSource>

/** Действие хука. Дискриминант — `type`. */
export const HookAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('chat'), message: z.string() }),
  z.object({ type: z.literal('reconnectAfter'), minutes: z.number().min(1).max(720) }),
  z.object({ type: z.literal('disconnect'), reason: z.string().optional() }),
  z.object({ type: z.literal('alert'), text: z.string() }),
  z.object({ type: z.literal('noop') })
])
export type HookAction = z.infer<typeof HookAction>

export const ServerHook = z.object({
  id: z.string(),
  name: z.string(),
  match: z.object({
    source: HookSource.default('chat'),
    pattern: z.string(), // regex, компилируется с таймаутом (ReDoS!)
    flags: z.string().default('i')
  }),
  action: HookAction,
  cooldownSec: z.number().default(30),
  enabled: z.boolean().default(true)
})
export type ServerHook = z.infer<typeof ServerHook>

export const AutoEat = z.object({
  enabled: z.boolean().default(true),
  startAtFood: z.number().min(1).max(19).default(16),
  bannedFoods: z.array(z.string()).default(['rotten_flesh', 'spider_eye', 'poisonous_potato'])
})
export type AutoEat = z.infer<typeof AutoEat>

/** Окно расписания: дни недели (0 = воскресенье) + HH:MM..HH:MM. */
export const ScheduleWindow = z.object({
  days: z.array(z.number().min(0).max(6)),
  from: z.string().regex(/^\d{2}:\d{2}$/),
  to: z.string().regex(/^\d{2}:\d{2}$/)
})
export type ScheduleWindow = z.infer<typeof ScheduleWindow>

export const Schedule = z.object({
  enabled: z.boolean().default(false),
  windows: z.array(ScheduleWindow).default([]),
  timezone: z.string().default('Europe/Kyiv')
})
export type Schedule = z.infer<typeof Schedule>

export const BehaviourConfig = z.object({
  antiAfk: AntiAfk.default({}),
  chunkAnchor: ChunkAnchor.default({}),
  hooks: z.array(ServerHook).default([]),
  autoEat: AutoEat.default({}),
  schedule: Schedule.default({})
})
export type BehaviourConfig = z.infer<typeof BehaviourConfig>

/** Полностью дефолтный конфиг поведений — то, что получает новый бот. */
export function defaultBehaviourConfig(): BehaviourConfig {
  return BehaviourConfig.parse({})
}
